import { useEffect, useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";

const tiers = [
  {
    name: "Flexible",
    label: "No commitment",
    desc: "For hosts who only wish to rent part-time or want maximum flexibility.",
    single: "20%",
    multiple: "18%",
    features: [
      "No minimum contract",
      "Airbnb & 16+ platform listing",
      "Dynamic pricing & yield management",
      "24/7 guest communication",
      "Housekeeping coordination",
      "Maintenance coordination",
      "Owner reporting & dashboard",
    ],
    popular: false,
  },
  {
    name: "1 Year",
    label: "12 months",
    desc: "The flexibility to review performance after your first year with us.",
    single: "18%",
    multiple: "16%",
    features: [
      "Airbnb & 16+ platform listing",
      "Dynamic pricing & yield management",
      "24/7 guest communication",
      "Housekeeping coordination",
      "Maintenance coordination",
      "Owner reporting & dashboard",
    ],
    popular: false,
  },
  {
    name: "2 Year",
    label: "24 months",
    desc: "Reduced fees and improved pricing stability for committed landlords.",
    single: "16%",
    multiple: "14%",
    features: [
      "Airbnb & 16+ platform listing",
      "Dynamic pricing & yield management",
      "24/7 guest communication",
      "Housekeeping coordination",
      "Maintenance coordination",
      "Owner reporting & dashboard",
    ],
    popular: true,
  },
  {
    name: "3 Year",
    label: "36 months",
    desc: "Our best-value option with the lowest fees and long-term performance optimisation.",
    single: "14%",
    multiple: "12%",
    features: [
      "Airbnb & 16+ platform listing",
      "Dynamic pricing & yield management",
      "24/7 guest communication",
      "Housekeeping coordination",
      "Maintenance coordination",
      "Owner reporting & dashboard",
    ],
    popular: false,
  },
];

const onboardingOptions = [
  {
    name: "Standard Onboarding",
    price: "£150",
    desc: "Professional short-let photographer with clean, well-lit, platform-optimised images. Meets Airbnb and Booking.com best practice standards. The most commonly selected option.",
    features: [
      "Experienced professional photographer",
      "Platform-optimised images",
      "Suitable for the majority of properties",
      "Meets Airbnb & Booking.com standards",
    ],
    recommended: false,
  },
  {
    name: "Elite Onboarding",
    price: "£450",
    desc: "Designed for owners who want to maximise performance and pricing from day one. Listings using Elite imagery typically achieve up to 15% higher nightly rates.",
    features: [
      "Specialist photographer with advanced editing",
      "Higher-quality composition",
      "Additional staging & styling during shoot",
      "Stronger visual storytelling",
      "Up to 15% higher nightly rates",
    ],
    recommended: true,
  },
];

function TierCard({ tier, onCta }) {
  const [hover, setHover] = useState(false);
  const dark = hover;
  const sub = dark ? "rgba(255,255,255,0.55)" : "#888";
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: dark ? "#1A1A1A" : "#fff",
        border: "1px solid #EBEBEB",
        borderColor: dark ? "#1A1A1A" : "#EBEBEB",
        borderRadius: 16,
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        boxShadow: dark ? "0 16px 40px rgba(0,0,0,0.18)" : "none",
        transform: dark ? "rotate(-1deg) scale(1.03)" : "rotate(0deg) scale(1)",
        zIndex: dark ? 2 : 1,
      }}
    >
      <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: dark ? "#fff" : "#1A1A1A", margin: "0 0 6px", letterSpacing: "-0.02em" }}>{tier.name}</h3>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "0 0 16px" }}>{tier.label}</p>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: sub, lineHeight: 1.5, margin: "0 0 20px" }}>{tier.desc}</p>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <div style={{ flex: 1, background: dark ? "rgba(255,255,255,0.08)" : "#F7F7F7", borderRadius: 10, padding: "12px", textAlign: "center" }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "0 0 4px" }}>1 Property</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 22, color: dark ? "#fff" : "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{tier.single}</p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "2px 0 0" }}>+VAT</p>
        </div>
        <div style={{ flex: 1, background: dark ? "rgba(255,255,255,0.08)" : "#F7F7F7", borderRadius: 10, padding: "12px", textAlign: "center" }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "0 0 4px" }}>2+ Properties</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 22, color: dark ? "#fff" : "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{tier.multiple}</p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "2px 0 0" }}>+VAT</p>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {tier.features.map((f) => (
          <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: dark ? "rgba(255,255,255,0.1)" : "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
              <Check size={10} color={dark ? "#fff" : "#555"} strokeWidth={2.5} />
            </div>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: dark ? "rgba(255,255,255,0.8)" : "#444", lineHeight: 1.4 }}>{f}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onCta}
        style={{
          marginTop: 24,
          background: dark ? "#fff" : "#1A1A1A",
          color: dark ? "#1A1A1A" : "#fff",
          border: "none",
          borderRadius: 8, padding: "13px 20px",
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
          cursor: "pointer", transition: "all 0.2s",
        }}
      >
        Get started
      </button>
    </div>
  );
}

function OnboardingCard({ opt, onCta }) {
  const [hover, setHover] = useState(false);
  const dark = hover;
  const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: dark ? "rgba(255,255,255,0.4)" : "#888", margin: "0 0 4px" };
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: dark ? "#1A1A1A" : "#fff",
        border: "1px solid #EBEBEB",
        borderRadius: 16,
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        boxShadow: dark ? "0 16px 40px rgba(0,0,0,0.18)" : "none",
        borderColor: dark ? "#1A1A1A" : "#EBEBEB",
        transform: dark ? "rotate(-1deg) scale(1.03)" : "rotate(0deg) scale(1)",
        zIndex: dark ? 2 : 1,
      }}
    >
      {opt.recommended && (
        <div style={{ position: "absolute", top: -12, left: 24, background: "#112215", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, padding: "5px 14px", borderRadius: 100, letterSpacing: "0.04em" }}>
          RECOMMENDED
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: dark ? "#fff" : "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{opt.name}</h3>
        <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 22, color: dark ? "#fff" : "#1A1A1A" }}>{opt.price}</span>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: dark ? "rgba(255,255,255,0.4)" : "#888", display: "block" }}>+VAT</span>
        </div>
      </div>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: dark ? "rgba(255,255,255,0.6)" : "#666", lineHeight: 1.6, margin: "0 0 20px" }}>{opt.desc}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {opt.features.map((f) => (
          <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: dark ? "rgba(255,255,255,0.1)" : "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
              <Check size={10} color={dark ? "#fff" : "#555"} strokeWidth={2.5} />
            </div>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: dark ? "rgba(255,255,255,0.85)" : "#444", lineHeight: 1.4 }}>{f}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onCta}
        style={{
          marginTop: 24,
          background: dark ? "#fff" : "#1A1A1A",
          color: dark ? "#1A1A1A" : "#fff",
          border: "none",
          borderRadius: 8, padding: "13px 20px",
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
          cursor: "pointer", transition: "all 0.2s",
        }}
      >
        Get started
      </button>
    </div>
  );
}

export default function Pricing() {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
  document.title = "Airbnb Management Pricing | From 12% + VAT | Truestays";
  const setMeta = (name, content) => {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  const setOgMeta = (property, content) => {
    let el = document.querySelector(`meta[property="${property}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  setMeta("description", "Transparent short-let management fees from 12% + VAT. Paid per booking, no monthly retainer, no long-term contract. See exactly what you pay. Free income estimate.");
  setOgMeta("og:type", "website");
  setOgMeta("og:title", "Airbnb Management Pricing | From 12% + VAT | Truestays");
  setOgMeta("og:description", "Transparent short-let management fees from 12% + VAT. Paid per booking, no monthly retainer, no long-term contract. See exactly what you pay. Free income estimate.");
  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", "Airbnb Management Pricing | From 12% + VAT | Truestays");
  setMeta("twitter:description", "Transparent short-let management fees from 12% + VAT. Paid per booking, no monthly retainer, no long-term contract. See exactly what you pay.");
    const canonical = document.querySelector("link[rel='canonical']") || document.createElement("link");
    canonical.rel = "canonical"; canonical.href = "https://www.truestays.co.uk/pricing";
    document.head.appendChild(canonical);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {/* Hero */}
      <section style={{
        backgroundColor: "#f4f2f0",
        paddingTop: "clamp(120px, 12vw, 160px)",
        paddingBottom: "clamp(120px, 11vw, 150px)",
        paddingLeft: "clamp(20px, 5vw, 80px)",
        paddingRight: "clamp(20px, 5vw, 80px)",
        textAlign: "center",
      }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", marginBottom: 14, marginTop: 0 }}>Pricing</p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(32px, 4.5vw, 56px)", color: "#1A1A1A", margin: "0 auto 16px", letterSpacing: "-0.03em", lineHeight: 1.1, maxWidth: 680 }}>
          Simple, transparent pricing.
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(16px, 2vw, 19px)", color: "#555", maxWidth: 520, margin: "0 auto 32px", lineHeight: 1.65 }}>
          No setup fees. No monthly retainers. No lock-in. We earn only when you earn.
        </p>
        <button
          onClick={() => setShowModal(true)}
          style={{ background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 10, padding: "15px 30px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
          onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
        >
          Get a free valuation <ArrowRight size={16} />
        </button>
      </section>

      {/* Management fee section */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(56px, 7vw, 96px) clamp(20px, 5vw, 80px)", marginTop: -80, position: "relative", zIndex: 10 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Management Fee</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(26px, 3.5vw, 40px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 auto 14px", lineHeight: 1.2 }}>
              From 12% + VAT
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>
              Our fee is a percentage of booking revenue. The longer you commit and the more properties you bring, the lower your rate.
            </p>
          </div>

          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }} className="pricing-grid">
            {tiers.map((tier) => (
              <TierCard key={tier.name} tier={tier} onCta={() => setShowModal(true)} />
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#F7F7F7", border: "1px solid #EBEBEB", borderRadius: 100, padding: "12px 22px" }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#444" }}>Managing <strong style={{ color: "#1A1A1A" }}>3+ properties</strong>? Portfolio landlords get a bespoke quote.</span>
              <button onClick={() => setShowModal(true)} style={{ background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 100, padding: "9px 18px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
                onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}>
                Get a bespoke quote
              </button>
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginTop: 16, marginBottom: 0 }}>
              All prices exclude VAT, charged at the standard rate. Prices are subject to change.
            </p>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section style={{ backgroundColor: "#F8F8F6", padding: "clamp(56px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Always Included</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 0 14px" }}>
              Everything in every plan.
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>
              No matter which plan you choose, these are always included - at no extra cost.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            {[
              "Multi-platform listing (Airbnb + 16 channels)",
              "Dynamic pricing & yield management",
              "24/7 guest communication & support",
              "Housekeeping coordination & linen",
              "Maintenance coordination",
              "Owner reporting & dashboard",
              "Professional photography coordination",
              "Direct booking engine access",
              "Guest vetting & ID verification",
              "Security deposit handling",
              "Automated check-in instructions",
              "Review management & guest ratings",
              "Calendar synchronisation across platforms",
              "Listing optimisation & A/B copy testing",
              "Channel manager & real-time availability",
              "Legal & compliance guidance",
              "Monthly performance reports",
              "Insurance & damage protection support",
              "Local market & competitor analysis",
              "Emergency on-call support line",
            ].map((item) => (
              <div key={item} style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 12, padding: "18px 20px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color="#555" strokeWidth={2.5} />
                </div>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#333", lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding section */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(56px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Onboarding</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 0 14px" }}>
              Choose your onboarding level.
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 560, margin: "0 auto 16px" }}>
              Your onboarding fee covers the core setup required to launch your property professionally.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="onboarding-grid">
            {onboardingOptions.map((opt) => (
              <OnboardingCard key={opt.name} opt={opt} onCta={() => setShowModal(true)} />
            ))}
          </div>
        </div>
      </section>



      {showModal && <EnrichmentModal mode="full" onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 900px) { .pricing-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 600px) { .pricing-grid { grid-template-columns: 1fr !important; } .onboarding-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}